import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ILocalAccount } from '@src/models/account';
import { AccountClientManager } from '@src/utils/accountClient';
import { AppContext } from '../App/AppContext';
import { IAuthContext, IAuthProvider } from './models';
import { useUnit } from 'effector-react';
import { userEffectorModel } from '@src/stores/user';
import { companyEffectorModel } from '@src/stores/company';
import { AnyType } from '@src/models/common';
import { mergeProfileAndAccounts } from '@src/stores/utils';
import { SESSION_CLIENT } from '@src/utils/sessionStorageClient';
import { getAccountNotificationName } from '@src/utils/sessionStorageClient/accounts';
import { NOTIFICATION_ERROR_DATA } from '@src/consts/notifications';
import { AuthContext } from '@centra/ui-global-context';

export const AuthProvider: FC<IAuthProvider> = ({ children, isAuthZone }) => {
  const [isReady, setIsReady] = useState(!isAuthZone);

  const { ngApiClient, setNotification } = useContext(AppContext);
  const [accountList, setAccountList] = useState<ILocalAccount[]>([]);

  const { fetchProfileFx, setUser, $profileStore } = useUnit(userEffectorModel);
  const { fetchCompanyDataFx } = useUnit(companyEffectorModel);

  const menuAccountList = useMemo(
    () => mergeProfileAndAccounts($profileStore, accountList),
    [$profileStore, accountList],
  );

  const updateAccountList = useCallback((list: AnyType[]) => {
    const updateListMap = new Map(list.map((u) => [u.id, u]));

    setAccountList((prev) => {
      const newList = prev.map((acc) => {
        const newAcc = updateListMap.get(acc.id) || {};

        return {
          ...acc,
          ...newAcc,
          active: acc.active,
        };
      });

      AccountClientManager.updateAccountList(newList);
      return newList;
    });
  }, []);

  const addAccount = useCallback(
    (email?: string) => {
      ngApiClient.toAddAccount(email);
    },
    [ngApiClient],
  );

  const changeAccount = useCallback(
    async (id: number) => {
      const { result, error } = await ngApiClient.changeAccount(id);

      if (result) {
        ngApiClient.toMainAuthPage();
      } else {
        let ntfFn = NOTIFICATION_ERROR_DATA.COMMON_ERROR;
        let params = '';
        if (error?.reason) {
          const user = menuAccountList.find((u) => u.id === id);
          params = getAccountNotificationName(user);
          ntfFn = NOTIFICATION_ERROR_DATA[error.reason];
        }

        setNotification(ntfFn(params));
      }
    },
    [menuAccountList, ngApiClient, setNotification],
  );

  const deleteAccount = useCallback(
    async (id: number) => {
      const result = await ngApiClient.deleteAccount(id);

      if (!result) return;

      const user = menuAccountList.find((u) => u.id === id);
      const displayName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : user?.email;

      AccountClientManager.deleteAccount(id);

      setNotification({
        view: 'success',
        title: `Аккаунт ${displayName} удален из списка`,
        content: undefined,
      });

      setAccountList((prev) => prev.filter((a) => a.id !== id));
    },
    [ngApiClient, menuAccountList, setNotification],
  );

  const logoutAndUnlinkAll = useCallback(() => {
    AccountClientManager.unlinkAccounts();

    ngApiClient.logout({ isRedirect: false });
  }, [ngApiClient]);

  useEffect(() => {
    const authorizing = async () => {
      const user = await ngApiClient.getUser();

      if (!user) {
        ngApiClient.toLogin();
        return;
      }

      setUser(user);
      /** Подгружаем пользователя и сохраняем в сторе */
      /** TODO: ВАЖНО!!! нужно перенести это в эффектор!!!!! сделать подписку на евент setUser */
      // Порядок не менять https://centrateam.atlassian.net/browse/CA-2737
      await fetchProfileFx({ ngApiClient });
      await fetchCompanyDataFx({ ngApiClient });

      /** После добавления или установки активного пользователя сразу же мы сохраняем список из localStorage в контекст */
      const newAccountList = AccountClientManager.getAccountList();
      newAccountList && setAccountList(newAccountList);

      setIsReady(true);
    };

    isAuthZone && authorizing();
  }, [fetchCompanyDataFx, fetchProfileFx, isAuthZone, ngApiClient, setUser]);

  /** Тут смотрим, вдруг есть какие нибудь нотификации которые нужно показать  */
  useEffect(() => {
    const notification = SESSION_CLIENT.getNotification();
    if (notification) {
      setNotification(notification);
    }
  }, [setNotification]);

  const value = useMemo<IAuthContext>(
    () => ({
      accountList,
      addAccount,
      changeAccount,
      deleteAccount,
      updateAccountList,
      menuAccountList,
      logoutAndUnlinkAll,
    }),
    [accountList, addAccount, changeAccount, deleteAccount, updateAccountList, menuAccountList, logoutAndUnlinkAll],
  );

  if (!isReady) return null;

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
